import { useState, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { AgendaEvent } from "@/types";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, startOfWeek, endOfWeek, isWithinInterval, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

const SCREEN_WIDTH = Dimensions.get("window").width;
const DAY_WIDTH = (SCREEN_WIDTH - 48) / 7;

/**
 * Theme colors type for memoized components
 */
type ThemeColors = ReturnType<typeof useThemeColors>;

/**
 * Props for AgendaCalendar
 */
interface AgendaCalendarProps {
  events: AgendaEvent[];
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
  isLoading?: boolean;
}

/**
 * Calendar header with navigation (memoized)
 */
const CalendarHeader = memo(({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onToday,
  colors,
}: {
  currentMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  colors: ThemeColors;
}) => (
  <View className="flex-row items-center justify-between px-4 py-3">
    <TouchableOpacity
      onPress={onPrevMonth}
      className="p-2 rounded-full"
      style={{ backgroundColor: colors.surfaceSecondary }}
      accessibilityLabel="Vorige maand"
      accessibilityRole="button"
    >
      <Ionicons name="chevron-back" size={20} color={colors.text} />
    </TouchableOpacity>

    <View className="flex-row items-center">
      <Text className="text-lg font-bold capitalize" style={{ color: colors.text }}>
        {format(currentMonth, "MMMM yyyy", { locale: nl })}
      </Text>
      <TouchableOpacity
        onPress={onToday}
        className="ml-3 px-3 py-1 rounded-full"
        style={{ backgroundColor: colors.primaryLight }}
        accessibilityLabel="Ga naar vandaag"
        accessibilityRole="button"
      >
        <Text className="text-sm font-medium" style={{ color: colors.primary }}>
          Vandaag
        </Text>
      </TouchableOpacity>
    </View>

    <TouchableOpacity
      onPress={onNextMonth}
      className="p-2 rounded-full"
      style={{ backgroundColor: colors.surfaceSecondary }}
      accessibilityLabel="Volgende maand"
      accessibilityRole="button"
    >
      <Ionicons name="chevron-forward" size={20} color={colors.text} />
    </TouchableOpacity>
  </View>
));

CalendarHeader.displayName = "CalendarHeader";

/**
 * Weekday header row (memoized)
 */
const WeekdayHeader = memo(({ colors }: { colors: ThemeColors }) => {
  // Monday-first week (European)
  const weekdays = ["ma", "di", "wo", "do", "vr", "za", "zo"];

  return (
    <View className="flex-row px-4 py-2">
      {weekdays.map((day) => (
        <View key={day} style={{ width: DAY_WIDTH }} className="items-center">
          <Text
            className="text-xs font-medium uppercase"
            style={{ color: colors.textTertiary }}
          >
            {day}
          </Text>
        </View>
      ))}
    </View>
  );
});

WeekdayHeader.displayName = "WeekdayHeader";

/**
 * Extended event info type for DayCell
 */
interface DayEventInfoProp {
  colors: string[];
  isWeekendStart: boolean;
  isWeekendEnd: boolean;
  isWeekendMiddle: boolean;
  isFamilieDay: boolean;
  weekendColor: string | null;
  familieDayColor: string | null;
}

/**
 * Single day cell (memoized)
 */
const DayCell = memo(({
  date,
  isSelected,
  isCurrentMonth,
  eventInfo,
  onPress,
  colors,
}: {
  date: Date;
  isSelected: boolean;
  isCurrentMonth: boolean;
  eventInfo: DayEventInfoProp;
  onPress: () => void;
  colors: ThemeColors;
}) => {
  const today = isToday(date);
  const hasEvents = eventInfo.colors.length > 0;
  const dayNumber = date.getDate();
  
  // Determine special styling
  const isWeekend = eventInfo.isWeekendStart || eventInfo.isWeekendEnd || eventInfo.isWeekendMiddle;
  const weekendColor = eventInfo.weekendColor || "#10B981";
  const familieDayColor = eventInfo.familieDayColor || "#F59E0B";

  /**
   * Get background styling for weekend range
   */
  const getWeekendStyle = () => {
    if (!isWeekend) return null;
    
    return {
      backgroundColor: `${weekendColor}20`,
      borderTopLeftRadius: eventInfo.isWeekendStart ? 18 : 0,
      borderBottomLeftRadius: eventInfo.isWeekendStart ? 18 : 0,
      borderTopRightRadius: eventInfo.isWeekendEnd ? 18 : 0,
      borderBottomRightRadius: eventInfo.isWeekendEnd ? 18 : 0,
      marginLeft: eventInfo.isWeekendStart ? 2 : 0,
      marginRight: eventInfo.isWeekendEnd ? 2 : 0,
    };
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[{ width: DAY_WIDTH }, getWeekendStyle()]}
      className="items-center py-1"
      accessibilityLabel={`${dayNumber}${hasEvents ? `, ${eventInfo.colors.length} events` : ""}${eventInfo.isFamilieDay ? ", familie dag" : ""}${isWeekend ? ", familie weekend" : ""}`}
      accessibilityRole="button"
    >
      <View
        className="w-9 h-9 rounded-full items-center justify-center"
        style={[
          isSelected && { backgroundColor: colors.primary },
          !isSelected && eventInfo.isFamilieDay && {
            borderWidth: 3,
            borderColor: familieDayColor,
            backgroundColor: `${familieDayColor}15`,
          },
          !isSelected && !eventInfo.isFamilieDay && today && {
            borderWidth: 2,
            borderColor: colors.primary,
          },
        ]}
      >
        <Text
          className="text-sm font-medium"
          style={{
            color: isSelected
              ? "#FFFFFF"
              : eventInfo.isFamilieDay
              ? familieDayColor
              : !isCurrentMonth
              ? colors.textTertiary
              : today
              ? colors.primary
              : colors.text,
          }}
        >
          {dayNumber}
        </Text>
      </View>

      {/* Event indicator dots */}
      <View className="flex-row mt-1 h-2 items-center justify-center">
        {/* Show weekend indicator */}
        {isWeekend && !eventInfo.isFamilieDay && (
          <View
            className="w-2 h-2 rounded-full mx-0.5"
            style={{ backgroundColor: weekendColor }}
          />
        )}
        {/* Show familie dag indicator (sun icon style) */}
        {eventInfo.isFamilieDay && (
          <View
            className="w-2 h-2 rounded-full mx-0.5"
            style={{ backgroundColor: familieDayColor }}
          />
        )}
        {/* Show regular event dots */}
        {eventInfo.colors.slice(0, isWeekend || eventInfo.isFamilieDay ? 2 : 3).map((color, index) => (
          <View
            key={index}
            className="w-1.5 h-1.5 rounded-full mx-0.5"
            style={{ backgroundColor: color }}
          />
        ))}
      </View>
    </TouchableOpacity>
  );
});

DayCell.displayName = "DayCell";

/**
 * AgendaCalendar Component
 * A native calendar view with event indicators
 */
export const AgendaCalendar = ({
  events,
  selectedDate,
  onSelectDate,
  onMonthChange,
  isLoading = false,
}: AgendaCalendarProps) => {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(selectedDate));

  /**
   * Special event info for a day (familie weekend, familie dag)
   */
  interface DayEventInfo {
    colors: string[];
    isWeekendStart: boolean;
    isWeekendEnd: boolean;
    isWeekendMiddle: boolean;
    isFamilieDay: boolean;
    weekendColor: string | null;
    familieDayColor: string | null;
  }

  /**
   * Pre-compute event info map for all days
   * Includes regular event colors and special event styling
   */
  const eventInfoMap = useMemo(() => {
    const map: Record<string, DayEventInfo> = {};
    
    events.forEach((event) => {
      const startDate = parseISO(event.starts_at);
      const endDate = event.ends_at ? parseISO(event.ends_at) : startDate;
      const eventColor = event.color || colors.primary;
      const isWeekendEvent = event.category === "family_weekend";
      const isFamilieDay = event.category === "family_day";

      // For multi-day events, add to all days in the range
      const daysInRange = eachDayOfInterval({ start: startDate, end: endDate });
      
      daysInRange.forEach((day, index) => {
        const dateKey = format(day, "yyyy-MM-dd");
        
        if (!map[dateKey]) {
          map[dateKey] = {
            colors: [],
            isWeekendStart: false,
            isWeekendEnd: false,
            isWeekendMiddle: false,
            isFamilieDay: false,
            weekendColor: null,
            familieDayColor: null,
          };
        }

        // Add regular event colors (dots)
        if (!isWeekendEvent && !isFamilieDay) {
          if (!map[dateKey].colors.includes(eventColor)) {
            map[dateKey].colors.push(eventColor);
          }
        }

        // Mark special events
        if (isWeekendEvent) {
          map[dateKey].weekendColor = eventColor;
          if (index === 0) {
            map[dateKey].isWeekendStart = true;
          } else if (index === daysInRange.length - 1) {
            map[dateKey].isWeekendEnd = true;
          } else {
            map[dateKey].isWeekendMiddle = true;
          }
        }

        if (isFamilieDay) {
          map[dateKey].isFamilieDay = true;
          map[dateKey].familieDayColor = eventColor;
        }
      });
    });
    
    return map;
  }, [events, colors.primary]);

  /**
   * Get event info for a specific date
   */
  const getEventInfoForDate = useCallback(
    (date: Date): DayEventInfo => {
      const dateKey = format(date, "yyyy-MM-dd");
      return eventInfoMap[dateKey] || {
        colors: [],
        isWeekendStart: false,
        isWeekendEnd: false,
        isWeekendMiddle: false,
        isFamilieDay: false,
        weekendColor: null,
        familieDayColor: null,
      };
    },
    [eventInfoMap]
  );

  /**
   * Generate calendar days for current month view
   * Uses Monday as first day of week (European standard)
   */
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    // Get start of week (Monday) for the first day of month
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    // Get end of week (Sunday) for the last day of month
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  /**
   * Navigate to previous month
   */
  const handlePrevMonth = useCallback(() => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    onMonthChange?.(newMonth);
  }, [currentMonth, onMonthChange]);

  /**
   * Navigate to next month
   */
  const handleNextMonth = useCallback(() => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    onMonthChange?.(newMonth);
  }, [currentMonth, onMonthChange]);

  /**
   * Navigate to today
   */
  const handleToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(startOfMonth(today));
    onSelectDate(today);
    onMonthChange?.(startOfMonth(today));
  }, [onSelectDate, onMonthChange]);

  /**
   * Handle date selection
   */
  const handleSelectDate = useCallback(
    (date: Date) => {
      onSelectDate(date);
    },
    [onSelectDate]
  );

  /**
   * Check if date is in current month
   */
  const isCurrentMonthDate = useCallback(
    (date: Date): boolean => {
      return (
        date.getMonth() === currentMonth.getMonth() &&
        date.getFullYear() === currentMonth.getFullYear()
      );
    },
    [currentMonth]
  );

  // Group days into weeks (7 days each)
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      <CalendarHeader
        currentMonth={currentMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        colors={colors}
      />

      <WeekdayHeader colors={colors} />

      {isLoading ? (
        <View className="h-64 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <View className="px-4 pb-4">
          {weeks.map((week, weekIndex) => (
            <View key={weekIndex} className="flex-row">
              {week.map((day) => (
                <DayCell
                  key={day.toISOString()}
                  date={day}
                  isSelected={isSameDay(day, selectedDate)}
                  isCurrentMonth={isCurrentMonthDate(day)}
                  eventInfo={getEventInfoForDate(day)}
                  onPress={() => handleSelectDate(day)}
                  colors={colors}
                />
              ))}
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

export default AgendaCalendar;

